import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { googleMeetApi } from "../../api/google-meet.api";
import { useAuthStore } from "../../store/auth.store";
function useConference(id: string, live = false) {
  const school = useAuthStore((s) => s.activeSchoolId),
    user = useAuthStore((s) => s.user?.id);
  return useQuery({
    queryKey: ["conference", school, user, id],
    queryFn: () => googleMeetApi.get(id),
    refetchInterval: live ? 15000 : false,
    staleTime: 30000,
    gcTime: 0,
    retry: false,
  });
}
export function JoinMeeting({ id }: { id: string }) {
  const q = useConference(id);
  if (q.isError || q.data?.state !== "READY" || !q.data.url) return null;
  return (
    <a
      className="btn btn--primary"
      href={q.data.url}
      target="_blank"
      rel="noopener noreferrer"
    >
      Rejoindre Meet
    </a>
  );
}
/** `open` : contenu déplié d'emblée (fenêtre « Google Meet » de la page Réunions). */
export function GoogleConnection({ open = false }: { open?: boolean } = {}) {
  const school = useAuthStore((s) => s.activeSchoolId),
    user = useAuthStore((s) => s.user?.id);
  const client = useQueryClient();
  const [notice, setNotice] = useState("");
  const q = useQuery({
    queryKey: ["google-connection", school, user],
    queryFn: googleMeetApi.status,
    gcTime: 0,
    retry: false,
  });
  const connect = useMutation({
    mutationFn: googleMeetApi.connect,
    onSuccess: (d) => {
      const u = new URL(d.url);
      if (u.origin === "https://accounts.google.com")
        window.location.assign(u.href);
    },
  });
  const disconnect = useMutation({
    mutationFn: googleMeetApi.disconnect,
    onSuccess: async (d) => {
      setNotice(
        d.remoteRevoked
          ? "Compte déconnecté."
          : "Accès Permio retiré. Vérifiez également les autorisations Permio dans votre compte Google.",
      );
      await client.invalidateQueries({ queryKey: ["google-connection"] });
      await client.invalidateQueries({ queryKey: ["conference"] });
    },
  });
  return (
    <details className="workspace-panel" open={open || undefined}>
      <summary>Mon compte Google Calendar</summary>
      {q.isError ? (
        <p role="alert">Connexion Google indisponible.</p>
      ) : !q.data ? (
        <p>Chargement…</p>
      ) : !q.data.configured ? (
        <p>
          La connexion Google doit être configurée par l’exploitant. Vous pouvez
          déjà ajouter un lien Meet existant à vos réunions.
        </p>
      ) : q.data.connected ? (
        <>
          <p>Connecté : {q.data.email}</p>
          <button
            className="btn btn--secondary"
            disabled={disconnect.isPending}
            onClick={() => disconnect.mutate()}
          >
            Déconnecter Google
          </button>
        </>
      ) : (
        <>
          <p>
            Créez les visioconférences dans votre calendrier principal. Vos
            notes et comptes rendus restent dans Permio.
          </p>
          <button
            className="btn btn--secondary"
            disabled={connect.isPending}
            onClick={() => connect.mutate()}
          >
            Connecter mon compte Google
          </button>
        </>
      )}
      <p>
        <a href="/privacy#google" target="_blank" rel="noopener noreferrer">
          Confidentialité de vos données Google
        </a>
      </p>
      {notice && <p role="status">{notice}</p>}
      {(connect.isError || disconnect.isError) && (
        <p role="alert">Opération impossible. Réessayez.</p>
      )}
    </details>
  );
}
